import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DndContext, DragOverlay, useDraggable, useDroppable, pointerWithin } from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  ChevronLeft, ChevronRight, Truck, Users, X, Clock, Building2, 
  Search, Maximize2, Minimize2, RefreshCw, GripVertical
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, subDays, parseISO } from 'date-fns';

// ============================================
// CONSTANTS
// ============================================
const TIMELINE_START_HOUR = 5;  // 5 AM
const TIMELINE_END_HOUR = 20;   // 8 PM
const SLOT_WIDTH = 40;          // 40px per 30-min slot
const ROW_HEIGHT = 80;          // Height of each job row
const HEADER_HEIGHT = 40;       // Height of the time header

// Generate time slots (30-minute intervals)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = TIMELINE_START_HOUR; hour < TIMELINE_END_HOUR; hour++) {
    slots.push({ hour, minute: 0, label: `${hour.toString().padStart(2, '0')}:00` });
    slots.push({ hour, minute: 30, label: `${hour.toString().padStart(2, '0')}:30` });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Convert time string (HH:MM) to pixel offset
const timeToPixel = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const totalMinutes = (hours - TIMELINE_START_HOUR) * 60 + minutes;
  return Math.max(0, (totalMinutes / 30) * SLOT_WIDTH);
};

// Convert duration (hours) to pixel width
const durationToPixel = (duration) => {
  if (!duration) return SLOT_WIDTH * 2; // Default 1 hour
  return (duration * 60 / 30) * SLOT_WIDTH;
};

// ============================================
// DRAGGABLE COMPONENTS
// ============================================

// Draggable Resource (Asset or Operator)
const DraggableResource = ({ id, type, data, children, isAllocated }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${type}-${id}`,
    data: { type, ...data }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...listeners} 
      {...attributes}
      className={`${isAllocated ? 'opacity-50' : ''}`}
    >
      {children}
    </div>
  );
};

// Droppable Gantt Bar (Job)
const DroppableGanttBar = ({ id, children, style }) => {
  const { setNodeRef, isOver } = useDroppable({ 
    id: `job-${id}`,
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`absolute transition-all duration-150 ${isOver ? 'ring-2 ring-[#ffd108] scale-[1.02] z-10' : ''}`}
      style={style}
    >
      {children}
    </div>
  );
};

// Allocation Bubble Component
const AllocationBubble = ({ type, label, onRemove }) => {
  const colorClasses = {
    equipment: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    operator: 'bg-green-500/20 text-green-400 border-green-500/30',
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${colorClasses[type]}`}>
            {type === 'equipment' && <Truck className="h-3 w-3" />}
            {type === 'operator' && <Users className="h-3 w-3" />}
            <span className="max-w-[60px] truncate">{label}</span>
            {onRemove && (
              <button 
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="ml-1 hover:text-red-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {type === 'equipment' ? 'Asset' : 'Operator'}: {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const PlanningBoard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timelineRef = useRef(null);
  
  // Core data
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [tenantSettings, setTenantSettings] = useState({ date_format: 'DD/MM/YYYY' });
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? parseISO(dateParam) : new Date();
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [operatorSearch, setOperatorSearch] = useState('');
  const [showAllocatedAssets, setShowAllocatedAssets] = useState(true);
  const [showAllocatedOperators, setShowAllocatedOperators] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeData, setActiveData] = useState(null);

  // Format date according to tenant settings
  const formatDateDisplay = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY': return `${day}-${monthShort}-${year}`;
      default: return `${day}/${month}/${year}`;
    }
  }, [tenantSettings.date_format]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobsRes, equipmentRes, operatorsRes, allocationsRes, customersRes, sitesRes, tenantRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/equipment'),
        api.get('/operators'),
        api.get('/allocations'),
        api.get('/customers'),
        api.get('/sites'),
        api.get(`/tenants/${user?.client_id}`)
      ]);
      setJobs(jobsRes.data);
      setEquipment(equipmentRes.data);
      setOperators(operatorsRes.data);
      setAllocations(allocationsRes.data);
      setCustomers(customersRes.data);
      setSites(sitesRes.data);
      if (tenantRes.data) {
        setTenantSettings({ date_format: tenantRes.data.date_format || 'DD/MM/YYYY' });
      }
    } catch (error) {
      console.error('Error loading planning data:', error);
      toast.error('Failed to load planning data');
    } finally {
      setLoading(false);
    }
  }, [api, user?.client_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scroll to current time on load
  useEffect(() => {
    if (!loading && timelineRef.current) {
      const now = new Date();
      const currentMinutes = (now.getHours() - TIMELINE_START_HOUR) * 60 + now.getMinutes();
      const scrollPosition = Math.max(0, (currentMinutes / 30) * SLOT_WIDTH - 200);
      timelineRef.current.scrollLeft = scrollPosition;
    }
  }, [loading]);

  // Get jobs for current date
  const jobsForDate = useMemo(() => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return jobs.filter(job => {
      const startDate = job.start_date;
      const endDate = job.end_date || job.start_date;
      return startDate <= dateStr && endDate >= dateStr;
    }).sort((a, b) => {
      // Sort by start time, then by job number
      if (a.start_time && b.start_time) return a.start_time.localeCompare(b.start_time);
      if (a.start_time) return -1;
      if (b.start_time) return 1;
      return (a.job_number || '').localeCompare(b.job_number || '');
    });
  }, [jobs, currentDate]);

  // Get allocation for a job on current date
  const getJobAllocation = useCallback((jobId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.find(a => 
      a.job_id === jobId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  // Get customer info
  const getCustomerInfo = useCallback((customerId) => {
    return customers.find(c => c.id === customerId);
  }, [customers]);

  // Get equipment info
  const getEquipmentInfo = useCallback((equipmentId) => {
    return equipment.find(e => e.id === equipmentId);
  }, [equipment]);

  // Get operator info
  const getOperatorInfo = useCallback((operatorId) => {
    return operators.find(o => o.id === operatorId);
  }, [operators]);

  // Check if asset is allocated for current date
  const isAssetAllocated = useCallback((assetId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.some(a => 
      a.equipment_id === assetId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  // Check if operator is allocated for current date
  const isOperatorAllocated = useCallback((operatorId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.some(a => 
      a.operator_id === operatorId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  // Filter assets
  const filteredAssets = useMemo(() => {
    // Include assets that are available, allocated, or have no status set (legacy data)
    let filtered = equipment.filter(e => 
      !e.status || e.status === 'available' || e.status === 'allocated'
    );
    
    if (assetSearch) {
      const search = assetSearch.toLowerCase();
      filtered = filtered.filter(e => 
        (e.asset_id || '').toLowerCase().includes(search) ||
        (e.name || '').toLowerCase().includes(search) ||
        (e.equipment_type || '').toLowerCase().includes(search)
      );
    }
    
    if (!showAllocatedAssets) {
      filtered = filtered.filter(e => !isAssetAllocated(e.id));
    }
    
    return filtered;
  }, [equipment, assetSearch, showAllocatedAssets, isAssetAllocated]);

  // Filter operators
  const filteredOperators = useMemo(() => {
    // Include operators that are active or have no status set (legacy data)
    // Exclude only explicitly inactive/terminated operators
    let filtered = operators.filter(o => 
      !o.status || o.status === 'active' || o.status === 'available'
    );
    
    if (operatorSearch) {
      const search = operatorSearch.toLowerCase();
      filtered = filtered.filter(o => 
        (o.name || '').toLowerCase().includes(search) ||
        (o.email || '').toLowerCase().includes(search)
      );
    }
    
    if (!showAllocatedOperators) {
      filtered = filtered.filter(o => !isOperatorAllocated(o.id));
    }
    
    return filtered;
  }, [operators, operatorSearch, showAllocatedOperators, isOperatorAllocated]);

  // Handle date navigation
  const navigateDate = (direction) => {
    setCurrentDate(prev => direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1));
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveData(active.data.current);
  };

  // Handle drag end - create or update allocation
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveData(null);

    if (!over) return;

    // Parse the active ID correctly - format is "type-uuid" where uuid contains hyphens
    const activeIdStr = active.id;
    const firstDash = activeIdStr.indexOf('-');
    const resourceType = activeIdStr.substring(0, firstDash);
    const resourceId = activeIdStr.substring(firstDash + 1);
    
    const jobId = over.id.replace('job-', '');
    
    // Find the job
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;

    // Get existing allocation for this job
    const existingAllocation = getJobAllocation(jobId);
    const dateStr = format(currentDate, 'yyyy-MM-dd');

    try {
      if (existingAllocation) {
        // Use PATCH for partial update
        const updateData = {};
        if (resourceType === 'equipment') {
          updateData.equipment_id = resourceId;
        } else if (resourceType === 'operator') {
          updateData.operator_id = resourceId;
        }

        await api.patch(`/allocations/${existingAllocation.id}`, updateData);
        toast.success(`${resourceType === 'equipment' ? 'Asset' : 'Operator'} assigned`);
      } else {
        // Create new allocation with empty strings for optional fields
        const allocationData = {
          job_id: jobId,
          start_date: dateStr,
          end_date: job.end_date && job.end_date !== job.start_date ? dateStr : null,
          equipment_id: resourceType === 'equipment' ? resourceId : '',
          operator_id: resourceType === 'operator' ? resourceId : '',
        };

        await api.post('/allocations', allocationData);
        toast.success(`${resourceType === 'equipment' ? 'Asset' : 'Operator'} allocated`);
      }

      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate');
    }
  };

  // Remove allocation (asset or operator)
  const handleRemoveFromAllocation = async (allocationId, field) => {
    try {
      const allocation = allocations.find(a => a.id === allocationId);
      if (!allocation) return;

      // Check if both fields would be empty after removal
      const otherField = field === 'equipment_id' ? 'operator_id' : 'equipment_id';
      const otherValue = allocation[otherField];
      
      if (!otherValue) {
        // If the other field is also empty, delete the allocation entirely
        await api.delete(`/allocations/${allocationId}`);
        toast.success('Allocation removed');
      } else {
        // Otherwise just clear the field using PATCH
        await api.patch(`/allocations/${allocationId}`, { [field]: '' });
        toast.success(`${field === 'equipment_id' ? 'Asset' : 'Operator'} removed`);
      }

      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'not_allocated': { bg: 'bg-zinc-700', border: 'border-zinc-500', text: 'text-zinc-300' },
      'allocated': { bg: 'bg-amber-500/30', border: 'border-amber-500', text: 'text-amber-400' },
      'confirmed': { bg: 'bg-blue-500/30', border: 'border-blue-500', text: 'text-blue-400' },
      'docketed': { bg: 'bg-purple-500/30', border: 'border-purple-500', text: 'text-purple-400' },
      'invoiced': { bg: 'bg-green-500/30', border: 'border-green-500', text: 'text-green-400' },
    };
    return colors[status] || colors['not_allocated'];
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate timeline width
  const timelineWidth = TIME_SLOTS.length * SLOT_WIDTH;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">Loading planning board...</p>
        </div>
      </div>
    );
  }

  return (
    <DndContext 
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`h-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background p-4' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/schedule')}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Schedule
            </Button>
            <h1 className="text-xl font-heading font-bold">Planning Board</h1>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigateDate('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[180px] text-center">
              <div className="font-bold">{format(currentDate, 'EEEE')}</div>
              <div className="text-sm text-muted-foreground">{formatDateDisplay(currentDate)}</div>
            </div>
            <Button variant="outline" size="icon" onClick={() => navigateDate('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={toggleFullscreen}>
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className="flex-1 grid grid-cols-[200px_1fr_200px] gap-4 min-h-0">
          
          {/* LEFT PANEL - Assets */}
          <Card className="card-industrial flex flex-col min-h-0">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Assets ({filteredAssets.length})
                </CardTitle>
              </div>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    placeholder="Search assets..."
                    value={assetSearch}
                    onChange={(e) => setAssetSearch(e.target.value)}
                    className="h-7 pl-7 text-xs"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllocatedAssets}
                    onChange={(e) => setShowAllocatedAssets(e.target.checked)}
                    className="rounded"
                  />
                  Show allocated
                </label>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {filteredAssets.map((asset) => {
                  const allocated = isAssetAllocated(asset.id);
                  return (
                    <DraggableResource 
                      key={asset.id} 
                      id={asset.id} 
                      type="equipment" 
                      data={asset}
                      isAllocated={allocated}
                    >
                      <div className={`p-2 rounded border transition-all hover:bg-muted/50 ${allocated ? 'border-blue-500/30 bg-blue-500/5' : 'border-border'}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{asset.asset_id}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {asset.equipment_type || asset.name || 'Unknown'}
                            </div>
                          </div>
                          {allocated && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">
                              In Use
                            </Badge>
                          )}
                        </div>
                      </div>
                    </DraggableResource>
                  );
                })}
                {filteredAssets.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    No assets found
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>

          {/* CENTER PANEL - Gantt Timeline */}
          <Card className="card-industrial flex flex-col min-h-0 overflow-hidden">
            <CardHeader className="py-3 px-4 border-b flex-shrink-0">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Jobs for {format(currentDate, 'EEEE, d MMM')} ({jobsForDate.length})
              </CardTitle>
            </CardHeader>
            
            {/* Timeline Container */}
            <div className="flex-1 overflow-auto" ref={timelineRef}>
              <div style={{ minWidth: timelineWidth + 150 }}>
                {/* Time Header */}
                <div className="sticky top-0 z-10 bg-background border-b flex" style={{ height: HEADER_HEIGHT }}>
                  <div className="w-[150px] flex-shrink-0 border-r bg-muted/30 flex items-center px-2 text-xs font-medium text-muted-foreground">
                    Job Info
                  </div>
                  <div className="flex">
                    {TIME_SLOTS.map((slot, idx) => (
                      <div 
                        key={idx} 
                        className={`flex-shrink-0 border-r flex items-end pb-1 justify-center text-[10px] text-muted-foreground ${slot.minute === 0 ? 'font-medium' : 'opacity-50'}`}
                        style={{ width: SLOT_WIDTH }}
                      >
                        {slot.minute === 0 ? slot.label : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Jobs */}
                {jobsForDate.length === 0 ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No jobs scheduled for this day</p>
                    </div>
                  </div>
                ) : (
                  jobsForDate.map((job) => {
                    const allocation = getJobAllocation(job.id);
                    const customer = getCustomerInfo(job.customer_id);
                    const statusColor = getStatusColor(job.status);
                    const assignedEquipment = allocation?.equipment_id ? getEquipmentInfo(allocation.equipment_id) : null;
                    const assignedOperator = allocation?.operator_id ? getOperatorInfo(allocation.operator_id) : null;
                    
                    // Calculate position and width
                    const leftOffset = timeToPixel(job.start_time || '07:00');
                    const barWidth = durationToPixel(job.est_duration || 2);

                    return (
                      <div key={job.id} className="flex border-b" style={{ height: ROW_HEIGHT }}>
                        {/* Job Info Column */}
                        <div className="w-[150px] flex-shrink-0 border-r p-2 bg-muted/10">
                          <div className="text-xs font-medium truncate">{customer?.company_name || 'Unknown'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{job.equipment_type}</div>
                          <Badge variant="outline" className={`text-[9px] mt-1 ${statusColor.text} border-current`}>
                            {job.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        {/* Timeline Area */}
                        <div className="flex-1 relative">
                          {/* Grid lines */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {TIME_SLOTS.map((slot, idx) => (
                              <div 
                                key={idx} 
                                className={`flex-shrink-0 border-r ${slot.minute === 0 ? 'border-zinc-600' : 'border-zinc-800'}`}
                                style={{ width: SLOT_WIDTH }}
                              />
                            ))}
                          </div>
                          
                          {/* Job Bar (Droppable) */}
                          <DroppableGanttBar 
                            id={job.id}
                            style={{ 
                              top: 8, 
                              left: leftOffset, 
                              width: barWidth,
                              height: ROW_HEIGHT - 16,
                              minWidth: 100
                            }}
                          >
                            <div 
                              className={`w-full h-full rounded-lg border-l-4 ${statusColor.bg} ${statusColor.border} transition-all cursor-pointer hover:brightness-110`}
                            >
                              <div className="p-2 h-full flex flex-col">
                                <div className="flex items-center gap-1 text-[10px]">
                                  <span className="font-mono font-bold">{job.start_time || '07:00'}</span>
                                  <span className="text-muted-foreground">
                                    {job.est_duration ? `(${job.est_duration}h)` : ''}
                                  </span>
                                  <Badge variant="outline" className="text-[8px] px-1 py-0 ml-auto">
                                    {job.job_number}
                                  </Badge>
                                </div>
                                
                                {/* Allocation Drop Zones */}
                                <div className="flex-1 flex items-center gap-1 mt-1">
                                  {assignedEquipment ? (
                                    <AllocationBubble
                                      type="equipment"
                                      label={assignedEquipment.asset_id}
                                      onRemove={() => handleRemoveFromAllocation(allocation.id, 'equipment_id')}
                                    />
                                  ) : (
                                    <div className="text-[9px] text-zinc-500 italic border border-dashed border-zinc-600 rounded px-1">
                                      + Asset
                                    </div>
                                  )}
                                  {assignedOperator ? (
                                    <AllocationBubble
                                      type="operator"
                                      label={assignedOperator.name}
                                      onRemove={() => handleRemoveFromAllocation(allocation.id, 'operator_id')}
                                    />
                                  ) : (
                                    <div className="text-[9px] text-zinc-500 italic border border-dashed border-zinc-600 rounded px-1">
                                      + Operator
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </DroppableGanttBar>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Card>

          {/* RIGHT PANEL - Operators */}
          <Card className="card-industrial flex flex-col min-h-0">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Operators ({filteredOperators.length})
                </CardTitle>
              </div>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    placeholder="Search operators..."
                    value={operatorSearch}
                    onChange={(e) => setOperatorSearch(e.target.value)}
                    className="h-7 pl-7 text-xs"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllocatedOperators}
                    onChange={(e) => setShowAllocatedOperators(e.target.checked)}
                    className="rounded"
                  />
                  Show allocated
                </label>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {filteredOperators.map((operator) => {
                  const allocated = isOperatorAllocated(operator.id);
                  return (
                    <DraggableResource 
                      key={operator.id} 
                      id={operator.id} 
                      type="operator" 
                      data={operator}
                      isAllocated={allocated}
                    >
                      <div className={`p-2 rounded border transition-all hover:bg-muted/50 ${allocated ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{operator.name}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {operator.worker_type || operator.email || 'Operator'}
                            </div>
                          </div>
                          {allocated && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-green-500/10 text-green-400 border-green-500/30">
                              In Use
                            </Badge>
                          )}
                        </div>
                      </div>
                    </DraggableResource>
                  );
                })}
                {filteredOperators.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    No operators found
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId && activeData && (
            <div className="p-2 rounded border bg-background shadow-lg">
              <div className="flex items-center gap-2">
                {activeData.type === 'equipment' ? (
                  <Truck className="h-4 w-4 text-blue-400" />
                ) : (
                  <Users className="h-4 w-4 text-green-400" />
                )}
                <span className="font-medium text-sm">
                  {activeData.type === 'equipment' ? activeData.asset_id : activeData.name}
                </span>
              </div>
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default PlanningBoard;
